import axios from 'axios'
import config from '@/config.js'
import exportConvert from '@/store/export.convert.js'
import {
  getStatic,
  getStaticDetail,
  readOnlyError,
} from '@/services/data-source.js'

const API_URL = config.apiUrl

const state = {
  scriptorium: null,
  scriptoriums: [],
  scriptoriumsAll: [],
}

const mutations = {
  setScriptorium(state, scriptorium){
    state.scriptorium = scriptorium
  },
  setScriptoriumsAll(state, scriptoriums){
    state.scriptoriumsAll = scriptoriums
  },
  setScriptoriums(state, scriptoriums){
    state.scriptoriums = scriptoriums
  },
}

const getters = {
}

const actions = {
  async loadScriptorium({ commit }, scriptoriumId){
    const request = config.staticData
      ? getStaticDetail('scriptorium', scriptoriumId)
      : axios.get(`${API_URL}/scriptorium/${scriptoriumId}`)
    return request
      .then(r => r.data)
      .then(async scriptorium => {
        if (config.staticData && Array.isArray(scriptorium)) {
          scriptorium = await exportConvert.convert('scriptorium.details', scriptorium)
        }
        scriptorium.manuscripts = scriptorium.manuscripts || {}
        commit('setScriptorium', scriptorium)
        return scriptorium
      })
  },
  loadScriptoriums({ commit }){
    const request = config.staticData
      ? getStatic('scriptoriums.json')
      : axios.get(`${API_URL}/scriptoriums/all`)
    return request
      .then(r => r.data)
      .then(async scriptoriums => {
        if (config.staticData && scriptoriums.length && Array.isArray(scriptoriums[0])) {
          scriptoriums = await Promise.all(
            scriptoriums.map(item => exportConvert.convert('scriptoriums', item))
          )
        }
        commit('setScriptoriumsAll', scriptoriums)
        return scriptoriums
      })
  },
  loadTokensGroup(_, parameters){
    if (config.staticData) {
      return readOnlyError('Scriptorium token grouping')
    }
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/scriptorium/tokens-group`, parameters)
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
     })
  },
  async searchScriptoriums({ commit }, options){
    let query = options.query
    const excludedIds = new Set((options.exclude || []).map(String))
    if (config.staticData) {
      const response = await getStatic('scriptoriums.json')
      const converted = await Promise.all(
        response.data.map(item => Array.isArray(item)
          ? exportConvert.convert('scriptoriums', item)
          : item)
      )
      const search = String(query || '').toLocaleLowerCase()
      const scriptoriums = converted
        .filter(item => !excludedIds.has(String(item.id)))
        .filter(item => [item.name, item.abbreviation].some(
          value => String(value || '').toLocaleLowerCase().includes(search)
        ))
        .sort((a, b) => String(a.abbreviation || '').localeCompare(String(b.abbreviation || '')))
        .slice(0, 50)
      commit('setScriptoriums', scriptoriums)
      return scriptoriums
    }
    const exclude = [...excludedIds].join(',')
    return axios
      .get(`${API_URL}/scriptoriums/autocomplete?query=${encodeURIComponent(query || '')}&exclude=${encodeURIComponent(exclude)}`)
      .then(r => r.data)
      .then(scriptoriums => {
        commit('setScriptoriums', scriptoriums)
        return scriptoriums
      })
  },
  saveScriptorium(_, scriptorium){
    if (!config.editable) {
      return readOnlyError('Saving a scriptorium')
    }
    let url = `${API_URL}/scriptorium`
    if (scriptorium.id) {
      url += `/${scriptorium.id}`
    }
    return new Promise((resolve) => {
      axios
        .post(url, scriptorium)
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  createScriptorium(_, scriptorium){
    if (!config.editable) {
      return readOnlyError('Creating a scriptorium')
    }
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/scriptorium`, scriptorium)
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  deleteScriptorium(_, scriptorium){
    if (!config.editable) {
      return readOnlyError('Deleting a scriptorium')
    }
    return new Promise((resolve) => {
      axios
        .delete(`${API_URL}/scriptorium/${scriptorium.id}`)
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  mergeScriptoria(_, { mainScriptoriumId, scriptoriumIds }){
    if (!config.editable) {
      return readOnlyError('Merging scriptoria')
    }
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/scriptoriums/merge`, { mainScriptoriumId, scriptoriumIds })
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
}

export default {
  state,
  getters,
  actions,
  mutations
}
